import { useEffect, useMemo, useState } from "react";
import type { RequirementSpec } from "../types";

const emptySpec: RequirementSpec = {
  id: "",
  title: "",
  description: "",
  required: true,
  weight: 1,
  applicableTypes: []
};

export default function AdminPage() {
  const [adminToken, setAdminToken] = useState("");
  const [tokenReady, setTokenReady] = useState(false);
  const [specs, setSpecs] = useState<RequirementSpec[]>([]);
  const [hasKey, setHasKey] = useState<boolean | null>(null);
  const [newKey, setNewKey] = useState("");
  const [draftSpec, setDraftSpec] = useState<RequirementSpec>(emptySpec);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<string[]>([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [modelLoading, setModelLoading] = useState(false);
  const DEFAULT_PROMPT = `你是资深产品需求评审助手。目标：识别需求类型，逐条对照规范检查，指出缺失、给建议，输出权重加权后的评分。遇到信息不足要标记为缺失并给出需要补充的内容。

评审时请注意：
1. 仔细查看提供的原型截图，分析页面布局、交互元素、信息架构
2. 结合截图内容判断需求完整性，而非仅依赖文字描述
3. 对于截图中可见但未在文档中说明的功能，标记为"需补充说明"
4. 对于文档中提到但截图未体现的功能，标记为"需补充原型"
5. 评分要客观，基于实际可见内容而非推测`;
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_PROMPT);
  const [promptIsDefault, setPromptIsDefault] = useState(true);

  const totalWeight = useMemo(
    () => specs.reduce((sum, s) => sum + Number(s.weight || 0), 0),
    [specs]
  );

  const authed = adminToken.trim().length > 0;

  const getAuthHeaders = () => (authed ? { "x-admin-token": adminToken } : {});

  const load = async () => {
    if (!authed) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    const headers = getAuthHeaders();
    try {
      const [specRes, keyRes, modelRes] = await Promise.all([
        fetch("/api/admin/specs", { headers }),
        fetch("/api/admin/key", { headers }),
        fetch("/api/admin/model", { headers })
      ]);
      if (!specRes.ok) throw new Error(await specRes.text());
      if (!keyRes.ok) throw new Error(await keyRes.text());
      if (!modelRes.ok) throw new Error(await modelRes.text());
      const specData = await specRes.json();
      const keyData = await keyRes.json();
      const modelData = await modelRes.json();
      setSpecs(specData.specs || []);
      setHasKey(Boolean(keyData.hasKey));
      setSelectedModel(modelData.model || "");
      
      // 尝试加载系统提示词，失败则使用默认值
      try {
        const promptRes = await fetch("/api/admin/systemprompt", { headers });
        if (promptRes.ok) {
          const promptData = await promptRes.json();
          setSystemPrompt(promptData.prompt || DEFAULT_PROMPT);
          setPromptIsDefault(promptData.isDefault !== false);
        }
      } catch {
        // 使用默认值
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载失败");
    } finally {
      setLoading(false);
    }
  };

  // 仅从 URL ?token= 获取令牌，否则弹窗输入
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");
    if (tokenFromUrl) {
      setAdminToken(tokenFromUrl);
      setTokenReady(true);
    }
    // 没有 URL token 则等待用户输入
  }, []);

  useEffect(() => {
    if (authed && tokenReady) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tokenReady]);

  const saveSpecs = async () => {
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const res = await fetch("/api/admin/specs", {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ specs })
      });
      if (!res.ok) throw new Error(await res.text());
      setMessage("规范已保存");
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setLoading(false);
    }
  };

  const saveKey = async () => {
    if (!newKey) {
      setError("请输入新的 OpenAI Key");
      return;
    }
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const res = await fetch("/api/admin/key", {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ key: newKey })
      });
      if (!res.ok) throw new Error(await res.text());
      setHasKey(true);
      setNewKey("");
      setMessage("Key 已更新");
    } catch (err) {
      setError(err instanceof Error ? err.message : "更新 Key 失败");
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    setModelLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/models", { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setModels(data.models || []);
      if (!selectedModel && data.models?.length) {
        setSelectedModel(data.models[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取模型列表失败");
    } finally {
      setModelLoading(false);
    }
  };

  const saveModel = async () => {
    if (!selectedModel) {
      setError("请选择或输入模型名称");
      return;
    }
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const res = await fetch("/api/admin/model", {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ model: selectedModel })
      });
      if (!res.ok) throw new Error(await res.text());
      setMessage(`已设置模型为 ${selectedModel}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "设置模型失败");
    } finally {
      setLoading(false);
    }
  };

  const savePrompt = async () => {
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const res = await fetch("/api/admin/systemprompt", {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ prompt: systemPrompt })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message || "系统提示词已保存");
      setPromptIsDefault(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存提示词失败");
    } finally {
      setLoading(false);
    }
  };

  const resetPrompt = async () => {
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const res = await fetch("/api/admin/systemprompt", {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ prompt: "" })
      });
      if (!res.ok) throw new Error(await res.text());
      // 重新加载以获取默认值
      const promptRes = await fetch("/api/admin/systemprompt", { headers: getAuthHeaders() });
      const promptData = await promptRes.json();
      setSystemPrompt(promptData.prompt || "");
      setPromptIsDefault(true);
      setMessage("已恢复默认提示词");
    } catch (err) {
      setError(err instanceof Error ? err.message : "恢复默认提示词失败");
    } finally {
      setLoading(false);
    }
  };

  const addSpec = () => {
    if (!draftSpec.title || !draftSpec.description) {
      setError("请填写标题和描述");
      return;
    }
    const id = draftSpec.id || crypto.randomUUID?.() || `spec-${Date.now()}`;
    setSpecs((prev) => [...prev, { ...draftSpec, id }]);
    setDraftSpec(emptySpec);
  };

  const removeSpec = (id: string) => {
    setSpecs((prev) => prev.filter((s) => s.id !== id));
  };

  const updateSpecField = (id: string, field: keyof RequirementSpec, value: any) => {
    setSpecs((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const renderTokenGate = () => (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
    >
      <div
        className="card"
        style={{ width: "420px", maxWidth: "90%", boxShadow: "0 15px 40px rgba(0,0,0,0.25)" }}
      >
        <h3 style={{ marginTop: 0 }}>请输入管理令牌</h3>
        <div className="field">
          <input
            value={adminToken}
            onChange={(e) => setAdminToken(e.target.value)}
            placeholder="与 ADMIN_TOKEN 一致"
            type="password"
            autoFocus
          />
        </div>
        <div className="actions" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn primary"
            onClick={() => setTokenReady(true)}
            disabled={!adminToken}
          >
            进入
          </button>
        </div>
        <div className="muted small">也可在 URL 加 ?token=xxx 直接访问，例如：/admin?token=xxx</div>
      </div>
    </div>
  );

  return (
    <div>
      {(!tokenReady || !authed) && renderTokenGate()}
      <h2 className="section-title">管理后台</h2>
      
      <div className="card" style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <div className="muted small">
          {loading ? "加载中..." : "数据已加载"}
        </div>
        <button className="btn outline" onClick={load} disabled={!authed || loading}>
          刷新数据
        </button>
      </div>

      <div style={{ marginTop: 16 }} className="card">
        <h3 style={{ marginTop: 0 }}>OpenAI Key 管理</h3>
        <div className="muted small">
          状态：{hasKey === null ? "加载中..." : hasKey ? "✅ 已配置" : "❌ 未配置"}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <label>新的 Key（仅写入，不回显）</label>
          <input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="sk-..."
            type="password"
          />
        </div>
        <div className="actions">
          <button className="btn primary" onClick={saveKey} disabled={!authed || loading}>
            更新 Key
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }} className="card">
        <h3 style={{ marginTop: 0 }}>模型选择</h3>
        <div className="muted small" style={{ marginBottom: 8 }}>
          当前模型：<strong style={{ color: selectedModel ? "#10b981" : "#ef4444" }}>
            {selectedModel || "未设置"}
          </strong>
        </div>
        <div className="muted small">
          需要已配置 Key 才能拉取模型列表；可手动输入模型名称。
        </div>
        <div className="actions">
          <button
            className="btn outline"
            onClick={fetchModels}
            disabled={!authed || modelLoading}
          >
            {modelLoading ? "拉取中..." : "拉取模型列表"}
          </button>
        </div>
        <div className="field">
          <label>选择模型</label>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="inline-input"
          >
            <option value="">请选择</option>
            {models.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>或手动输入模型名称</label>
          <input
            className="inline-input"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            placeholder="例如 gpt-4.1, gpt-4.1-mini, o3-mini 等"
          />
        </div>
        <div className="actions">
          <button className="btn primary" onClick={saveModel} disabled={!authed || loading}>
            保存模型
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }} className="card">
        <h3 style={{ marginTop: 0 }}>系统提示词（背景设定）</h3>
        <div className="muted small" style={{ marginBottom: 12 }}>
          调用 AI 时预设传入的基础指令，定义 AI 的角色和评审方式。
          {promptIsDefault ? (
            <span style={{ color: "#10b981", marginLeft: 8 }}>● 当前为默认</span>
          ) : (
            <span style={{ color: "#f59e0b", marginLeft: 8 }}>● 已自定义</span>
          )}
        </div>
        <div className="field">
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder="输入系统提示词..."
            style={{
              minHeight: 180,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
              lineHeight: 1.6
            }}
          />
        </div>
        <div className="actions" style={{ gap: 8 }}>
          <button className="btn primary" onClick={savePrompt} disabled={!authed || loading}>
            保存提示词
          </button>
          <button
            className="btn outline"
            onClick={resetPrompt}
            disabled={!authed || loading || promptIsDefault}
          >
            恢复默认
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }} className="card">
        <h3 style={{ marginTop: 0 }}>规范管理</h3>
        <div className="muted small">总权重：{totalWeight}</div>
        <table className="table">
          <thead>
            <tr>
              <th>标题</th>
              <th>描述</th>
              <th>必填</th>
              <th>权重</th>
              <th>适用需求类型</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {specs.map((spec) => (
              <tr key={spec.id}>
                <td>
                  <input
                    className="inline-input"
                    value={spec.title}
                    onChange={(e) => updateSpecField(spec.id, "title", e.target.value)}
                  />
                </td>
                <td>
                  <textarea
                    className="inline-input"
                    value={spec.description}
                    onChange={(e) =>
                      updateSpecField(spec.id, "description", e.target.value)
                    }
                  />
                </td>
                <td style={{ textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={spec.required}
                    onChange={(e) => updateSpecField(spec.id, "required", e.target.checked)}
                  />
                </td>
                <td>
                  <input
                    className="inline-input"
                    type="number"
                    min={0}
                    step={0.1}
                    value={spec.weight}
                    onChange={(e) =>
                      updateSpecField(spec.id, "weight", Number(e.target.value))
                    }
                  />
                </td>
                <td>
                  <input
                    className="inline-input"
                    value={(spec.applicableTypes || []).join(",")}
                    onChange={(e) =>
                      updateSpecField(
                        spec.id,
                        "applicableTypes",
                        e.target.value
                          .split(",")
                          .map((x) => x.trim())
                          .filter(Boolean)
                      )
                    }
                    placeholder="用逗号分隔类型关键词"
                  />
                </td>
                <td>
                  <button className="btn outline small" onClick={() => removeSpec(spec.id)}>
                    删除
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <input
                  className="inline-input"
                  value={draftSpec.title}
                  onChange={(e) => setDraftSpec({ ...draftSpec, title: e.target.value })}
                  placeholder="标题"
                />
              </td>
              <td>
                <textarea
                  className="inline-input"
                  value={draftSpec.description}
                  onChange={(e) =>
                    setDraftSpec({ ...draftSpec, description: e.target.value })
                  }
                  placeholder="描述"
                />
              </td>
              <td style={{ textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={draftSpec.required}
                  onChange={(e) =>
                    setDraftSpec({ ...draftSpec, required: e.target.checked })
                  }
                />
              </td>
              <td>
                <input
                  className="inline-input"
                  type="number"
                  min={0}
                  step={0.1}
                  value={draftSpec.weight}
                  onChange={(e) =>
                    setDraftSpec({ ...draftSpec, weight: Number(e.target.value) })
                  }
                />
              </td>
              <td>
                <input
                  className="inline-input"
                  value={(draftSpec.applicableTypes || []).join(",")}
                  onChange={(e) =>
                    setDraftSpec({
                      ...draftSpec,
                      applicableTypes: e.target.value
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean)
                    })
                  }
                  placeholder="用逗号分隔类型关键词"
                />
              </td>
              <td>
                <button className="btn primary small" onClick={addSpec} disabled={!authed}>
                  添加
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="actions">
          <button className="btn primary" onClick={saveSpecs} disabled={!authed || loading}>
            保存规范
          </button>
        </div>
      </div>

      {message && <div className="success" style={{ marginTop: 12 }}>{message}</div>}
      {error && <div className="danger" style={{ marginTop: 12 }}>{error}</div>}
    </div>
  );
}
